import React, { useCallback } from 'react';
import { EventCard } from '@sympla/sympla-components';
import { createPropsToTrackingMatrix } from 'components/Integration/createPropsToTrackingMatrix';
import {
  cardTypeTrackingSearch,
  componentTrackingSearch,
  typeTrackingSearch
} from 'constants/search';
import { MATRIX_TYPE } from 'constants/index';

const extractNumericId = (objectID: string): string =>
  objectID.replace(/\D/g, '');

const formatTagCard = (tag: string | undefined): string | undefined => {
  if (!tag) return undefined;
  return `${tag.charAt(0).toUpperCase()}${tag.slice(1).toLowerCase()}`;
};

interface TrackingConfig {
  trackingPrefix?: string;
  matrizTemplate?: string;
  componentTemplate?: string;
  cardType?: string;
}

interface AnalyticsClickParams {
  eventName: string;
  eventId: string;
  position: number;
  eventCategory?: string;
}

interface AlgoliaSearchHitProps {
  hit: IHit;
  tagCard?: string;
  tagVariant?: 'default' | 'dark';
  highlight?: boolean;
  layoutCard?: string;
  topEvent?: boolean;
  isHover?: boolean;
  tracking?: TrackingConfig;
  handleTrackAnalyticsClick?: (params: AnalyticsClickParams) => void;
  sendAlgoliaEvent?: () => void;
}

export function AlgoliaSearchHit({
  hit,
  tagCard,
  tagVariant = 'default',
  highlight = false,
  layoutCard = 'lateral-on-mobile',
  topEvent = false,
  tracking,
  handleTrackAnalyticsClick,
  isHover = true,
  sendAlgoliaEvent
}: AlgoliaSearchHitProps) {
  const {
    objectID,
    name,
    organizer,
    images,
    event_url,
    start_date,
    end_date,
    __position,
    location,
    start_date_formats,
    end_date_formats,
    type,
    __queryID,
    order,
    theme
  } = hit;
  const position = order ?? __position;
  const numberId = extractNumericId(objectID);

  const { dataCreative } = createPropsToTrackingMatrix(position, {
    trackingPrefix: tracking?.trackingPrefix || MATRIX_TYPE.SEARCH,
    matrizTemplate: tracking?.matrizTemplate || 'search_result',
    componentTemplate:
      tracking?.componentTemplate || componentTrackingSearch.term,
    dataLocation: null,
    cardType: tracking?.cardType || cardTypeTrackingSearch.eventCardSearch,
    type: typeTrackingSearch.event,
    templateType: 'algolia',
    payloadCard: hit,
    serviceParams: {
      queryID: __queryID,
      position: position
    }
  });

  const handleTrackAnalyticsClickRecommendedEvent = useCallback(() => {
    handleTrackAnalyticsClick?.({
      eventName: name,
      eventId: numberId,
      position: position,
      eventCategory: theme
    });

    if (__queryID && sendAlgoliaEvent) {
      sendAlgoliaEvent();
    }
  }, [
    name,
    numberId,
    position,
    __queryID,
    objectID,
    handleTrackAnalyticsClick
  ]);

  const eventStartDate = new Date(start_date * 1000).toISOString();
  const eventEndDate = new Date(end_date * 1000).toISOString();

  return (
    <EventCard
      id={numberId}
      name={name}
      images={images}
      sectionPosition={position}
      dataCreative={dataCreative}
      isHover={isHover}
      location={{
        name: location?.name,
        city: location?.city,
        state: location?.state
      }}
      organizer={organizer}
      type={type}
      url={`${event_url}?algoliaID=${__queryID}`}
      data
      start_date={eventStartDate}
      start_date_formats={start_date_formats}
      end_date={eventEndDate}
      end_date_formats={end_date_formats}
      layoutCard={layoutCard}
      tagCard={formatTagCard(tagCard)}
      tagVariant={tagVariant}
      highlight={highlight}
      onClick={handleTrackAnalyticsClickRecommendedEvent}
      topEvent={topEvent}
    />
  );
}
